/**
 * HelpFeedbackWidget — anonymous "did this help?" capture.
 *
 * Renders 👍 / 👎 buttons + an optional one-line note input on the
 * thumbs-down branch. POSTs to /api/help/feedback. Stores a random
 * fingerprint in localStorage so the user can change their mind
 * (the backend rewrites their previous vote) but spammers can't pad
 * the count by clicking 50 times.
 *
 * Design:
 *   - Subtle. Lives at the bottom of help pages, not nagging.
 *   - One-pass UX: vote → "Thanks." state. No modals, no toasts.
 *   - Counter shows after vote so user gets a tiny social-proof
 *     payoff ("you and 47 others found this helpful").
 *   - Fully optional — failing silently is preferred over surfacing
 *     a backend hiccup on a help page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ThumbsUp, ThumbsDown, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// localStorage keys are namespaced per page so a user who finds two
// different help pages helpful gets to vote on each.
const fpKey = (page) => `gs_help_fp_${page}`;
const voteKey = (page) => `gs_help_vote_${page}`;

const ensureFingerprint = (page) => {
  try {
    const existing = window.localStorage.getItem(fpKey(page));
    if (existing) return existing;
    // 18 random bytes → 24-char base64url. Plenty of entropy for
    // dedupe without being a tracking surface.
    const arr = new Uint8Array(18);
    window.crypto.getRandomValues(arr);
    const fp = btoa(String.fromCharCode(...arr))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    window.localStorage.setItem(fpKey(page), fp);
    return fp;
  } catch {
    // localStorage unavailable (private mode etc) — generate an
    // ephemeral fingerprint per render. Means dedupe is best-effort,
    // which is fine.
    return Math.random().toString(36).slice(2, 18);
  }
};

const readPriorVote = (page) => {
  try {
    return window.localStorage.getItem(voteKey(page));
  } catch {
    return null;
  }
};

const writeVote = (page, vote) => {
  try {
    window.localStorage.setItem(voteKey(page), vote);
  } catch {
    /* non-fatal */
  }
};

export const HelpFeedbackWidget = ({ page }) => {
  const [busy, setBusy] = useState(false);
  const [vote, setVote] = useState(() => readPriorVote(page));
  const [note, setNote] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [stats, setStats] = useState(null);

  // Lazy-load stats only after the user has voted, so the page
  // stays light for read-only visitors.
  useEffect(() => {
    if (!vote) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/help/feedback/stats`, {
          params: { page },
        });
        if (!cancelled) setStats(res.data);
      } catch {
        /* swallow — stats are decorative */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [vote, page]);

  const submit = async (helpful, withNote) => {
    if (busy) return;
    setBusy(true);
    try {
      const fingerprint = ensureFingerprint(page);
      await axios.post(`${API}/help/feedback`, {
        page,
        helpful,
        fingerprint,
        note: withNote ? (note || '').trim().slice(0, 240) || null : null,
      });
      const stored = helpful ? 'up' : 'down';
      writeVote(page, stored);
      setVote(stored);
      setShowNoteInput(false);
    } catch {
      // Silent failure — help-page feedback isn't critical UX.
    } finally {
      setBusy(false);
    }
  };

  // Already-voted state: thank-you card with optional counter.
  if (vote) {
    return (
      <div
        className="rounded-md border border-emerald-400/20 bg-emerald-400/[0.04] p-5 flex items-start gap-3"
        data-testid="help-feedback-thanks"
      >
        <CheckCircle2
          className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5"
          strokeWidth={1.5}
        />
        <div className="text-sm text-slate-300 leading-relaxed">
          <span className="text-white font-semibold">Thanks for the signal.</span>{' '}
          We use this to decide which "explain the policy" pages to
          invest in next.
          {stats && stats.total > 0 && (
            <div
              className="mt-2 text-[11px] text-slate-500 font-mono"
              data-testid="help-feedback-stats"
            >
              {stats.helpful} found this helpful · {stats.not_helpful} did not ·{' '}
              {stats.total} total
            </div>
          )}
        </div>
      </div>
    );
  }

  // Not voted yet.
  return (
    <div
      className="rounded-md border border-white/10 bg-[#0A0A0F] p-5"
      data-testid="help-feedback-widget"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-slate-300 mr-auto">
          Did this page help you understand why?
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={() => submit(true, false)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/10 bg-white/[0.02] hover:bg-emerald-400/10 hover:border-emerald-400/40 hover:text-emerald-300 text-sm transition-colors disabled:opacity-50"
          data-testid="help-feedback-up"
          aria-label="This page was helpful"
        >
          <ThumbsUp className="w-4 h-4" />
          Yes
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setShowNoteInput((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-white/10 bg-white/[0.02] hover:bg-red-400/10 hover:border-red-400/40 hover:text-red-300 text-sm transition-colors disabled:opacity-50"
          data-testid="help-feedback-down"
          aria-label="This page was not helpful"
          aria-expanded={showNoteInput}
        >
          <ThumbsDown className="w-4 h-4" />
          Not really
        </button>
      </div>

      {showNoteInput && (
        <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
          <label
            htmlFor="help-feedback-note"
            className="block text-xs text-slate-400"
          >
            What were you hoping to find? (optional, 240 chars)
          </label>
          <textarea
            id="help-feedback-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 240))}
            rows={2}
            placeholder="One line is fine — it goes to the team that owns this page."
            className="w-full text-sm bg-black/40 border border-white/10 rounded-md p-3 text-slate-200 placeholder:text-slate-600 focus:border-white/30 focus:outline-none resize-none"
            data-testid="help-feedback-note-input"
            disabled={busy}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => submit(false, true)}
              className="px-4 py-2 rounded-md bg-red-500/15 hover:bg-red-500/25 border border-red-400/30 text-red-300 text-sm transition-colors disabled:opacity-50"
              data-testid="help-feedback-down-submit"
            >
              Send feedback
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => submit(false, false)}
              className="px-4 py-2 rounded-md text-slate-500 hover:text-slate-300 text-sm transition-colors disabled:opacity-50"
              data-testid="help-feedback-down-skip"
            >
              Skip — just record the vote
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
